/**
 * Node-level registration approval for the superadmin (#35).
 *
 * Reads GET /api/users/registrations/pending with the current user id. That
 * endpoint is superadmin-only, so a 403 doubles as the "am I the server
 * admin" signal: we expose `isServerAdmin=false` and callers hide the panel.
 * Any other error is surfaced via `error`.
 *
 *   const { pending, isServerAdmin, loading, approve, reject } = usePendingRegistrations();
 */

import { useState, useEffect, useCallback } from 'react';
import { useApi } from './use_api';
import { useAuth } from '../context/AuthContext';

export function usePendingRegistrations() {
    const api = useApi();
    const { user } = useAuth();
    const userId = user?.id;

    const [pending, setPending] = useState([]);
    const [isServerAdmin, setIsServerAdmin] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = useCallback(() => {
        if (!userId) { setLoading(false); return; }
        setLoading(true);
        api.get('/api/users/registrations/pending', { params: { user_id: userId } })
            .then(data => {
                setPending(Array.isArray(data) ? data : []);
                setIsServerAdmin(true);
                setError(null);
            })
            .catch(err => {
                if (err.status === 403) {
                    setIsServerAdmin(false);   // not the server admin — panel stays hidden
                } else {
                    setError(err);
                }
            })
            .finally(() => setLoading(false));
    }, [api, userId]);

    useEffect(() => { load(); }, [load]);

    const act = useCallback((targetUserId, action) => {
        const path = `/api/users/registrations/${encodeURIComponent(targetUserId)}/${action}`;
        return api.post(path, undefined, { params: { user_id: userId } }).then(() => load());
    }, [api, userId, load]);

    return {
        pending,
        isServerAdmin,
        loading,
        error,
        refresh: load,
        approve: targetUserId => act(targetUserId, 'approve'),
        reject: targetUserId => act(targetUserId, 'reject'),
    };
}

export default usePendingRegistrations;
